import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { CodeInput } from '@/components/room/CodeInput';
import { Button } from '@/components/ui/Button';
import { api } from '@/api';
import { ApiError } from '@/api/apiClient';
import { humanizeJoinError } from '@/api/errors';
import s from './JoinPage.module.css';

type Phase = 'input' | 'connecting' | 'error';

const CODE_REGEX = /^[A-Z0-9]{6}$/;

export function JoinPage() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const initialCode = (searchParams.get('code') ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);

  const [code, setCode] = useState(initialCode);
  const [phase, setPhase] = useState<Phase>('input');
  const [errorMsg, setErrorMsg] = useState('');
  const submittingRef = useRef(false);

  const submit = async (raw: string) => {
    const normalized = raw.trim().toUpperCase();
    if (!CODE_REGEX.test(normalized)) return;
    if (submittingRef.current) return;
    submittingRef.current = true;
    setPhase('connecting');
    try {
      await api.rooms.join(normalized);
      nav(`/r/${normalized}`);
    } catch (e) {
      setErrorMsg(e instanceof ApiError ? humanizeJoinError(e) : 'Не удалось войти в комнату');
      setPhase('error');
      submittingRef.current = false;
    }
  };

  useEffect(() => {
    if (initialCode && CODE_REGEX.test(initialCode)) {
      void submit(initialCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={s.page}>
      <header className={s.header}>
        <button className={s.backBtn} onClick={() => nav('/')} aria-label="Назад">←</button>
        <span className="kicker">Войти по коду</span>
      </header>

      {phase === 'input' && (
        <>
          <h1 className={s.title}>Введи код комнаты</h1>
          <div className={s.inputWrap}>
            <CodeInput
              value={code}
              onChange={setCode}
              onComplete={() => void submit(code)}
            />
          </div>
          <div className={s.sticky}>
            <Button
              variant="primary"
              disabled={code.length !== 6}
              onClick={() => void submit(code)}
              style={{ width: '100%' }}
            >
              Подключиться
            </Button>
          </div>
        </>
      )}

      {phase === 'connecting' && (
        <div className={s.center}>
          <div className={s.dots}>
            <span className={`${s.dot} ${s.dot1}`} />
            <span className={`${s.dot} ${s.dot2}`} />
            <span className={`${s.dot} ${s.dot3}`} />
          </div>
          <p className={s.connectingText}>Подключаемся…</p>
          <p className={s.connectingCode}>{code}</p>
        </div>
      )}

      {phase === 'error' && (
        <div className={s.center}>
          <div className={s.errorCircle}>!</div>
          <p className={s.errorText}>{errorMsg}</p>
          <Button variant="primary" onClick={() => setPhase('input')} style={{ width: '100%', marginBottom: '12px' }}>
            Попробовать снова
          </Button>
          <Button variant="ghost" onClick={() => nav('/')} style={{ width: '100%' }}>
            На главную
          </Button>
        </div>
      )}
    </div>
  );
}
